import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { Card } from '../components/Card';
import { KpiCard } from '../components/KpiCard';
import { Pill } from '../components/Pill';
import { Tabs } from '../components/Tabs';
import { Icon } from '../components/Icons';
import { PayoutMethodModal } from '../components/PayoutMethodModal';
import { btnGhost, btnIcon, tableStyle, tdStyle, thStyle } from '../components/buttons';
import { creatorApi } from '../api/creator';
import { fmtDate, fmtUSD } from '../lib/format';
import { downloadCsv } from '../lib/csv';

const TAB_IDS = ['earnings', 'payouts', 'method', 'tax'] as const;
type TabId = (typeof TAB_IDS)[number];

const CURRENT_YEAR = new Date().getUTCFullYear();
const YEAR_OPTIONS = [CURRENT_YEAR, CURRENT_YEAR - 1, CURRENT_YEAR - 2];

export function EarningsScreen() {
  const navigate = useNavigate();
  const { tab: rawTab } = useParams<{ tab?: TabId }>();
  const tab: TabId = TAB_IDS.includes(rawTab as TabId) ? (rawTab as TabId) : 'earnings';
  const [year, setYear] = useState(CURRENT_YEAR);

  const totals = useQuery({ queryKey: ['creator', 'totals'], queryFn: creatorApi.totals });
  const months = useQuery({
    queryKey: ['creator', 'earnings-months', year],
    queryFn: () => creatorApi.earningsMonths(year),
  });
  const payouts = useQuery({ queryKey: ['creator', 'payouts'], queryFn: creatorApi.payouts });
  const taxDocs = useQuery({ queryKey: ['creator', 'tax-docs'], queryFn: creatorApi.taxDocs });

  const exportCsv = () => {
    const rows = (months.data ?? []).map((r) => [
      r.periodMonth,
      (r.amountCents / 100).toFixed(2),
      r.newSubs,
      r.renewals,
      r.status,
    ]);
    downloadCsv(`snapmon-earnings-${year}.csv`, ['Month', 'Earnings (USD)', 'New subs', 'Renewals', 'Status'], rows);
  };

  const tabsRight = (
    <div style={{ display: 'flex', gap: 8 }}>
      <label style={{ ...btnGhost, position: 'relative', cursor: 'pointer' }}>
        <Icon.Calendar />
        Year {year}
        <Icon.Chevron />
        <select
          value={year}
          onChange={(e) => setYear(Number(e.target.value))}
          aria-label="Select year"
          style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer', width: '100%' }}
        >
          {YEAR_OPTIONS.map((y) => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
      </label>
      <button style={btnGhost} onClick={exportCsv} disabled={(months.data ?? []).length === 0}>
        <Icon.Download />
        Export CSV
      </button>
    </div>
  );

  const t = totals.data;
  const avgPerMonth = (months.data ?? []).length
    ? Math.round((months.data ?? []).reduce((s, r) => s + r.amountCents, 0) / Math.max(1, (months.data ?? []).length))
    : 0;
  const paidOut = (payouts.data ?? []).filter((p) => p.status === 'paid').reduce((s, p) => s + p.amountCents, 0);

  return (
    <div style={{ padding: '24px 28px 60px', display: 'grid', gap: 18, maxWidth: 1280 }}>
      {/* KPI summary */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14 }}>
        <KpiCard label="Your lifetime earnings" value={t?.lifetimeEarningsCents ?? 0} format={fmtUSD} decimals={2} accent sublabel="Across all your codes" />
        <KpiCard label="Paid out (all-time)" value={paidOut} format={fmtUSD} decimals={2} sublabel="Sent to your bank" />
        <KpiCard label="Pending" value={t?.pendingPayoutCents ?? 0} format={fmtUSD} decimals={2} sublabel={t?.nextPayoutDate ? `Auto-pays ${fmtDate(t.nextPayoutDate)}` : ''} />
        <KpiCard label="Your avg / month" value={avgPerMonth} format={fmtUSD} decimals={0} sublabel="Trailing 12 months" />
      </div>

      <Card padding={0}>
        <div style={{ padding: '6px 20px 0' }}>
          <Tabs
            value={tab}
            onChange={(id) => navigate(id === 'earnings' ? '/earnings' : `/earnings/${id}`)}
            tabs={[
              { id: 'earnings', label: 'Earnings by month', count: months.data?.length },
              { id: 'payouts', label: 'Payouts', count: payouts.data?.length },
              { id: 'method', label: 'Payout method' },
              { id: 'tax', label: 'Tax documents', count: taxDocs.data?.length },
            ]}
            right={tabsRight}
          />
        </div>
        {tab === 'earnings' && <EarningsMonthsTab rows={months.data ?? []} />}
        {tab === 'payouts' && <PayoutsTab />}
        {tab === 'method' && <PayoutMethodTab />}
        {tab === 'tax' && <TaxTab />}
      </Card>
    </div>
  );
}

function EarningsMonthsTab({ rows }: { rows: { periodMonth: string; label: string; amountCents: number; newSubs: number; renewals: number; status: 'accruing' | 'paid' | 'pending' }[] }) {
  const codeShare = useQuery({ queryKey: ['creator', 'code-share'], queryFn: creatorApi.earningsCodeShare });
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.4fr) minmax(280px, 1fr)', borderTop: '1px solid var(--line)' }} className="snapmon-earntab">
      <div style={{ overflowX: 'auto' }}>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Month</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Your earnings</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>New subs</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Renewals</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => {
              const prev = rows[i + 1]?.amountCents ?? row.amountCents;
              const delta = prev > 0 ? ((row.amountCents - prev) / prev) * 100 : 0;
              const isCurrent = row.status === 'accruing';
              return (
                <tr key={row.periodMonth} style={{ borderTop: '1px solid var(--line)' }}>
                  <td style={tdStyle}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontWeight: 600 }}>{row.label}</span>
                      {isCurrent && <Pill tone="brand" size="sm">current</Pill>}
                    </div>
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'right' }} className="num">
                    <div style={{ fontWeight: 600 }}>{fmtUSD(row.amountCents)}</div>
                    {!isCurrent && prev > 0 && (
                      <div style={{ fontSize: 11, color: delta >= 0 ? 'var(--good)' : 'var(--bad)', fontFamily: 'var(--font-mono)' }}>
                        {delta >= 0 ? '+' : ''}{delta.toFixed(1)}%
                      </div>
                    )}
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'right', color: 'var(--ink-2)' }} className="num">{row.newSubs}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', color: 'var(--ink-2)' }} className="num">{row.renewals}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>
                    {isCurrent ? <Pill tone="warn">Accruing</Pill> : <Pill tone="good">Paid</Pill>}
                  </td>
                </tr>
              );
            })}
            {rows.length === 0 && (
              <tr><td colSpan={5} style={{ ...tdStyle, color: 'var(--ink-3)', textAlign: 'center', padding: 40 }}>No earnings yet this year.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <div style={{ borderLeft: '1px solid var(--line)', padding: 20, background: 'var(--bg-sunken)', display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div>
          <div style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 14 }}>Where your cut comes from</div>
          <div style={{ color: 'var(--ink-3)', fontSize: 12, marginTop: 2 }}>This month · by code</div>
        </div>
        <div style={{ display: 'flex', height: 10, borderRadius: 999, overflow: 'hidden', background: 'var(--bg-elev)' }}>
          {(codeShare.data ?? []).map((c, i) => (
            <div
              key={c.code}
              style={{ width: c.pct * 100 + '%', background: `color-mix(in oklab, var(--brand) ${100 - i * 22}%, var(--brand-2))` }}
            />
          ))}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {(codeShare.data ?? []).map((c, i) => (
            <div key={c.code} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13 }}>
              <div style={{ width: 10, height: 10, borderRadius: 2, background: `color-mix(in oklab, var(--brand) ${100 - i * 22}%, var(--brand-2))` }} />
              <div style={{ flex: 1, fontFamily: 'var(--font-mono)' }}>{c.code}</div>
              <div className="num" style={{ color: 'var(--ink-2)' }}>{(c.pct * 100).toFixed(0)}%</div>
            </div>
          ))}
          {(codeShare.data ?? []).length === 0 && (
            <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>No commission attributed yet this month.</div>
          )}
        </div>
      </div>
    </div>
  );
}

function PayoutsTab() {
  const payouts = useQuery({ queryKey: ['creator', 'payouts'], queryFn: creatorApi.payouts });
  const rows = payouts.data ?? [];
  return (
    <div style={{ borderTop: '1px solid var(--line)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 20px', background: 'var(--bg-sunken)', borderBottom: '1px solid var(--line)', fontSize: 12.5, color: 'var(--ink-2)' }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
          <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.6" />
          <path d="M12 7 v6 M12 16 h0.01" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
        Payouts are processed automatically on the 1st of each month — you don't need to request them.
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Payout ID</th>
              <th style={thStyle}>Period</th>
              <th style={thStyle}>Method</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Amount</th>
              <th style={thStyle}>Status</th>
              <th style={thStyle}></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((p) => (
              <tr key={p.id} style={{ borderTop: '1px solid var(--line)', background: p.status === 'scheduled' ? 'var(--brand-soft)' : undefined }}>
                <td style={tdStyle}><span className="mono" style={{ fontWeight: 600 }}>{p.publicId}</span></td>
                <td style={{ ...tdStyle, color: 'var(--ink-2)' }}>{fmtDate(p.periodMonth)}</td>
                <td style={{ ...tdStyle, color: 'var(--ink-2)' }}>{p.methodLabel}</td>
                <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }} className="num">{fmtUSD(p.amountCents)}</td>
                <td style={tdStyle}>
                  {p.status === 'paid' && <Pill tone="good">Paid</Pill>}
                  {p.status === 'scheduled' && <Pill tone="warn">Auto-scheduled</Pill>}
                  {p.status === 'processing' && <Pill tone="warn">Processing</Pill>}
                  {p.status === 'failed' && <Pill tone="bad">Failed</Pill>}
                  {p.status === 'skipped' && <Pill tone="neutral">Skipped</Pill>}
                </td>
                <td style={{ ...tdStyle, textAlign: 'right' }}>
                  {p.receiptUrl ? (
                    <a href={p.receiptUrl} target="_blank" rel="noreferrer" style={btnIcon} title="View receipt"><Icon.Download /></a>
                  ) : (
                    <span style={{ color: 'var(--ink-3)', fontSize: 12 }}>—</span>
                  )}
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={6} style={{ ...tdStyle, color: 'var(--ink-3)', textAlign: 'center', padding: 40 }}>No payouts yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PayoutMethodTab() {
  const method = useQuery({ queryKey: ['creator', 'payout-method'], queryFn: creatorApi.payoutMethod });
  const m = method.data;
  const [editOpen, setEditOpen] = useState(false);
  return (
    <div style={{ borderTop: '1px solid var(--line)', padding: 24, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }} className="snapmon-payout-method">
      <Card padding={20} style={{ background: 'var(--bg-sunken)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ fontSize: 11, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600 }}>Active method</div>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 600, marginTop: 6 }}>
              {m?.type === 'paypal' ? 'PayPal' : 'Bank transfer (ACH)'}
            </div>
          </div>
          {m?.verified ? <Pill tone="good">Verified</Pill> : <Pill tone="warn">Pending verification</Pill>}
        </div>
        <div style={{ marginTop: 14, fontFamily: 'var(--font-mono)', fontSize: 14, color: 'var(--ink-2)' }}>
          {m?.type === 'bank'
            ? `${m.bankName ?? 'Bank'} · ••${m.bankLast4 ?? '----'}`
            : m?.type === 'paypal'
              ? m.paypalEmail
              : 'No method on file'}
        </div>
        {m?.bankHolder && (
          <div style={{ marginTop: 4, fontSize: 12, color: 'var(--ink-3)' }}>{m.bankHolder} · routing on file</div>
        )}
        <div style={{ marginTop: 18, display: 'flex', gap: 8 }}>
          <button style={btnGhost} onClick={() => setEditOpen(true)}>
            {m ? 'Update method' : 'Add method'}
          </button>
        </div>
      </Card>
      <Card padding={20}>
        <div style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 16 }}>Payout schedule</div>
        <div style={{ color: 'var(--ink-3)', fontSize: 12, marginTop: 2 }}>When earnings move to your bank</div>
        <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {[
            { k: 'Frequency', v: 'Monthly' },
            { k: 'Payout day', v: '1st of each month' },
            { k: 'Minimum threshold', v: '$50.00' },
            { k: 'Currency', v: 'USD' },
            { k: 'Hold period', v: '15 days after renewal' },
          ].map((row) => (
            <div key={row.k} style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px dashed var(--line)', paddingTop: 8 }}>
              <span style={{ color: 'var(--ink-3)', fontSize: 13 }}>{row.k}</span>
              <span style={{ fontSize: 13, fontWeight: 500 }}>{row.v}</span>
            </div>
          ))}
        </div>
      </Card>
      <PayoutMethodModal open={editOpen} onClose={() => setEditOpen(false)} current={m} />
    </div>
  );
}

function TaxTab() {
  const docs = useQuery({ queryKey: ['creator', 'tax-docs'], queryFn: creatorApi.taxDocs });
  const onDownload = async (id: number) => {
    try {
      const r = await creatorApi.taxDocDownload(id);
      window.open(r.url, '_blank', 'noopener');
    } catch (err) {
      alert((err as Error)?.message ?? 'Download failed');
    }
  };
  return (
    <div style={{ borderTop: '1px solid var(--line)', padding: 24 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12 }}>
        {(docs.data ?? []).map((d) => (
          <Card key={d.id} padding={18} style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{ width: 44, height: 44, borderRadius: 'var(--r-md)', background: 'var(--brand-soft)', color: 'var(--brand-ink)', display: 'grid', placeItems: 'center', fontWeight: 700, fontFamily: 'var(--font-mono)', fontSize: 11 }}>
              PDF
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600 }}>{d.year} · {d.formType}</div>
              <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>Issued {fmtDate(d.issuedAt)} · {Math.round(d.fileSizeBytes / 1024)} KB</div>
            </div>
            <button style={btnGhost} onClick={() => onDownload(d.id)}><Icon.Download />Download</button>
          </Card>
        ))}
        {(docs.data ?? []).length === 0 && !docs.isLoading && (
          <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>No tax documents yet. Forms are generated automatically each January.</div>
        )}
      </div>
    </div>
  );
}
